import { Application } from "../../../_Bootstrap/Application";
import {
    addNoteToSale,
    addOrderReference,
    cancelSale,
    overrideSaleMetaData,
    preventSaleFromBeingCancelled,
    updateProduct,
} from "../../../Actions/SellScreen/SellScreenActions";
import { addCustomerToSale, removeCustomerFromSale } from "../../../Actions/SellScreen/SellScreenCustomerActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../Actions/SellScreen/SellScreenPaymentActions";
import { addProductToSale, removeProductFromSale } from "../../../Actions/SellScreen/SellScreenProductActions";
import { type SalePaymentStatus, type SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { uppercaseFirst } from "../../Generic/Strings";

interface EmbeddedSaleUpdateChanges {
    PRODUCT_ADD: {
        id: string;
        quantity: number;
        price?: number;
        consolidate?: boolean;
        indexAddress?: Array<number>;
        metaData: Record<string, unknown>;
    };
    PRODUCT_REMOVE: {
        id: string;
        indexAddress: Array<number>;
    };
    PAYMENT_ADD: {
        id: string;
        amount: number;
        cashout?: number | boolean;
        status?: SalePaymentStatus;
        options?: Partial<Exclude<SaleAddPaymentOptions, "status" | "cashout">>;
    };
    PAYMENT_REVERSE: {
        id: string;
        amount: number;
        cashout?: number;
        status?: SalePaymentStatus;
    };
    SALE_CANCEL: {};
    CUSTOMER_ADD: {
        id: string;
    };
    CUSTOMER_REMOVE: {};
    SALE_EXTERNAL_NOTE: {
        note: string;
        append?: boolean;
    };
    SALE_INTERNAL_NOTE: {
        note: string;
        append?: boolean;
    };
    SALE_ORDER_REFERENCE: {
        reference: string;
    };
    SALE_META_DATA: {
        metaData: Record<string, unknown>;
    };
    SALE_PREVENT_CANCELLATION: {
        preventCancellation: boolean;
    };
    PRODUCT_UPDATE: {
        id: string;
        indexAddress: Array<number>;
        metaData?: Record<string, unknown>;
        quantity?: number;
        price?: number;
    };
}

export interface EmbeddedSaleUpdateData {
    properties: [
        keyof EmbeddedSaleUpdateChanges, // Change type
        EmbeddedSaleUpdateChanges[keyof EmbeddedSaleUpdateChanges], // Change data
    ];
    events: [];
    type: "SaleUpdate";
}

export class EmbeddedSaleUpdate {
    protected static withinPreFinishSalePipeline = false;
    protected change: keyof EmbeddedSaleUpdateChanges;
    protected data: EmbeddedSaleUpdateChanges[keyof EmbeddedSaleUpdateChanges];

    constructor(dataFromEmbedded: EmbeddedSaleUpdateData) {
        this.change = dataFromEmbedded.properties[0];
        this.data = dataFromEmbedded.properties[1];
    }

    protected getProductFromSale(
        data: EmbeddedSaleUpdateChanges["PRODUCT_REMOVE" | "PRODUCT_UPDATE"]
    ): SaleProduct | false {
        // Check to make sure the ID matches the product at the address
        const state = Application.get("Redux").getState();
        const sale = state.sellscreen.sale;
        let product: SaleProduct | false = false;

        for(let i = 0, l = data.indexAddress.length; i < l; i++) {
            product = sale.products[data.indexAddress[i]];

            if(typeof product === "undefined") {
                console.error("Embedded: Invalid index address specified (not found)");

                return false;
            }
        }

        if(!product || product.uuid !== data.id) {
            console.error("Embedded: Invalid index address specified (product doesn't match)");

            return false;
        }

        return product;
    }

    protected applyProductAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_ADD"];

        Application.get("Redux").dispatch(addProductToSale(data.id, {
            quantity    : data.quantity,
            price       : data.price,
            consolidate : data.consolidate,
            indexAddress: data.indexAddress,
            metaData    : data.metaData || {},
        }));
    }

    protected applyProductRemove(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_REMOVE"];

        // No matching product found in sale
        if(!this.getProductFromSale(data)) {
            return;
        }

        // Remove the product from the sale
        Application.get("Redux").dispatch(removeProductFromSale(data.indexAddress));
    }

    protected applyPaymentAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PAYMENT_ADD"];

        Application.get("Redux").dispatch(addPaymentToSale(data.id, data.amount, {
            ...(data.options ?? {}),
            cashout : data.cashout,
            status  : data.status ?? "auto",
            // Specifying the `vouchers` option allows the Embedded API to overpay the sale (useful for triggering
            // refunds on already made payments due to a voucher being added that has since been rejected)
            vouchers: data.options?.vouchers ?? EmbeddedSaleUpdate.withinPreFinishSalePipeline,
        }));
    }

    protected applyPaymentReverse(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PAYMENT_REVERSE"];

        const sale = Application.get("Redux").getState().sellscreen.sale;

        if(!sale) {
            return;
        }

        let related: SaleAddPaymentOptions["related"] = undefined;

        for(let i = 0, l = sale.payments.length; i < l; i++) {
            const payment = sale.payments[i];

            if(
                payment.status !== "completed" ||
                payment.reverted ||
                payment.method !== data.id ||
                payment.amount !== data.amount ||
                payment.cashout !== data.cashout
            ) {
                continue;
            }

            related = {
                ...payment,
                index: i,
            };
        }

        Application.get("Redux").dispatch(addPaymentToSale(data.id, data.amount * -1, {
            cashout  : typeof data.cashout === "number" ? data.cashout * -1 : undefined,
            status   : data.status ?? "auto",
            refund   : true,
            reverting: true,
            related,
        }));
    }

    protected applySaleCancel(): void {
        // const data = this.data as EmbeddedSaleUpdateChanges["SALE_CANCEL"];

        Application.get("Redux").dispatch(cancelSale());
    }

    protected applyCustomerAdd(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["CUSTOMER_ADD"];

        // NOTE: Currently overrides the current customer
        Application.get("Redux").dispatch(addCustomerToSale(data.id, undefined, true));
    }

    protected applyCustomerRemove(): void {
        // const data = this.data as EmbeddedSaleUpdateChanges["CUSTOMER_REMOVE"];

        Application.get("Redux").dispatch(removeCustomerFromSale());
    }

    protected applySaleExternalNote(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_EXTERNAL_NOTE"];

        let note = "";

        if(data.append) {
            const state = Application.get("Redux").getState();

            note = state.sellscreen.sale.notes.sale;
        }

        note = `${note}${data.note}`;

        Application.get("Redux").dispatch(addNoteToSale("sale", note));
    }

    protected applySaleInternalNote(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_INTERNAL_NOTE"];

        let note = "";

        if(data.append) {
            const state = Application.get("Redux").getState();

            note = state.sellscreen.sale.notes.internal;
        }

        note = `${note}${data.note}`;

        Application.get("Redux").dispatch(addNoteToSale("internal", note));
    }

    protected applySaleOrderReference(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_ORDER_REFERENCE"];

        Application.get("Redux").dispatch(addOrderReference(data.reference));
    }

    protected applySaleMetaData(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_META_DATA"];

        Application.get("Redux").dispatch(overrideSaleMetaData(data.metaData));
    }

    protected applySalePreventCancellation(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["SALE_PREVENT_CANCELLATION"];

        Application.get("Redux").dispatch(preventSaleFromBeingCancelled(data.preventCancellation));
    }

    protected applyProductUpdate(): void {
        const data = this.data as EmbeddedSaleUpdateChanges["PRODUCT_UPDATE"];

        const product = this.getProductFromSale(data);

        // No matching product found in sale
        if(!product) {
            return;
        }

        const dataToUpdate: Partial<SaleProduct> = {};

        if(data.quantity) {
            dataToUpdate.quantity = data.quantity;
        }

        if(data.price) {
            dataToUpdate.prices = {
                ...product.prices,
                price: data.price,
            };
        }

        if(data.metaData) {
            dataToUpdate.metaData = data.metaData;
        }

        Application.get("Redux").dispatch(updateProduct(
            data.indexAddress,
            dataToUpdate
        ));
    }

    public apply(): void {
        const change = this.change.toString().toLowerCase();
        const split = change.split("_");

        let method = "apply";

        for(let i = 0, l = split.length; i < l; i++) {
            method = `${method}${uppercaseFirst(split[i])}`;
        }

        if(typeof this[method as keyof EmbeddedSaleUpdate] !== "function") {
            console.error("Unknown embedded sale update requested", this.change, this.data);

            return;
        }

        this[method as keyof EmbeddedSaleUpdate]();
    }

    /**
     * Set whether we're within the pre-finish sale pipeline
     */
    public static setWithinPreFinishSalePipeline(withinPipeline: boolean): void {
        this.withinPreFinishSalePipeline = withinPipeline;
    }
}
